import React from 'react'
// @ts-ignore
import { formatFloatAsCurrencySymbol } from '../../common/helpers.mjs'
// @ts-ignore
import { serviceDetails, currencyObject } from '../../common/types/types'
import { productType } from '../../types/types'
import { useQuoteContext } from '../../hooks/useQuoteContext'

/**
 * MultiProductTable is a React functional component that renders a table displaying a list of products
 * along with their quantities and total prices. It allows users to input quantities for each product,
 * dynamically updating the total price and the details list. This component is useful for managing
 * product selections and their associated details in a structured table format.
 *
 * @param {MultiProductTableProps} props - The properties object.
 * @param {string} props.title - The title of the table, displayed as the header of the first column.
 * @param {productType[]} props.productList - An array of products to be displayed in the table.
 * @param {any[]} props.detailsList - The list of details associated with the products (e.g., quantities and codes).
 * @param {React.Dispatch<React.SetStateAction<any[]>>} props.setDetailsList - A state setter function to update the details list.
 * @param {function} props.handleDetailsChange - A callback function triggered when the details list is updated.
 * @param {string} props.codeField - The field name used to match product codes in the details list (e.g., "serviceCode").
 * @param {currencyObject} props.currencyObject - An object containing currency-related information for formatting prices.
 * @param {number} [props.quantityStep=1] - The step value for the quantity input field, determining the increment/decrement value.
 * @param {boolean} [props.showPrice=true] - A flag indicating whether to display the product price in the table.
 */

interface MultiProductTableProps {
   title: string
   productList: productType[]
   detailsList: any[] // The list of details (e.g., professionalServiceDetails or timeClockDetails)
   setDetailsList: React.Dispatch<React.SetStateAction<any[]>>
   handleDetailsChange: (updatedList: any[]) => void
   codeField: string // The field name for the code (e.g., "serviceCode" or "timeClockCode")
   currencyObject: currencyObject
   quantityStep?: number
   showPrice?: boolean
}

function MultiProductTable({
   title,
   productList,
   detailsList,
   setDetailsList,
   handleDetailsChange,
   codeField,
   currencyObject,
   quantityStep = 1,
   showPrice = true,
}: MultiProductTableProps) {
   const { getString } = useQuoteContext()
   function getProductDetails(product: productType) {
      return detailsList.find(
         (detail) => detail[codeField] === product.ProductCode,
      )
   }

   function handleProductDetailsChange(product: productType, value: number) {
      const foundDetail = detailsList.find(
         (detail) => detail[codeField] === product.ProductCode,
      )
      if (!foundDetail) {
         const updatedList = [
            ...detailsList,
            { [codeField]: product.ProductCode, count: value },
         ]
         setDetailsList(updatedList)
         handleDetailsChange(updatedList)
      } else {
         const updatedList = detailsList.map((detail) =>
            detail[codeField] === product.ProductCode
               ? { ...detail, count: value }
               : detail,
         )
         setDetailsList(updatedList)
         handleDetailsChange(updatedList)
      }
   }

   return (
      <div className='row'>
         <div className='col'>
            <table className='table table-sm'>
               <thead>
                  <tr>
                     <th className='grey-bold col-bhr-clocks'>{title}</th>
                     <th className='grey-bold'>{getString('global.Qty')}</th>
                     <th
                        className='grey-bold'
                        style={{
                           width: '200px',
                           textAlign: 'center',
                           paddingRight: '90px',
                        }}
                     >
                        {getString('global.Total')}
                     </th>
                  </tr>
               </thead>
               <tbody>
                  {productList.map((product, index) => {
                     return (
                        <tr key={index}>
                           <td className='col-bhr-clocks'>
                              {product.ProductName}{' '}
                              {showPrice && (
                                 <span>
                                    &#40;
                                    {formatFloatAsCurrencySymbol(
                                       product.BasePrice,
                                       currencyObject,
                                    )}
                                    &#41;
                                 </span>
                              )}
                           </td>
                           <td>
                              <input
                                 className='form-control input-field-0'
                                 type='number'
                                 min={0}
                                 step={quantityStep}
                                 value={
                                    getProductDetails(product)
                                       ? getProductDetails(product).count
                                       : 0
                                 }
                                 onChange={(e) =>
                                    handleProductDetailsChange(
                                       product,
                                       parseFloat(e.target.value),
                                    )
                                 }
                                 onBlur={(e) => {
                                    const parsedValue = parseFloat(
                                       e.target.value,
                                    )
                                    if (isNaN(parsedValue))
                                       return handleProductDetailsChange(
                                          product,
                                          0,
                                       )

                                    const remainder = parsedValue % quantityStep
                                    let flooredValue = parsedValue
                                    // If the value is not a multiple of quantityStep, floor it to the nearest multiple
                                    if (remainder !== 0)
                                       flooredValue = parsedValue - remainder

                                    handleProductDetailsChange(
                                       product,
                                       flooredValue < 0 ? 0 : flooredValue,
                                    )
                                 }}
                              />
                           </td>
                           <td
                              style={{
                                 width: '200px',
                                 textAlign: 'right',
                                 paddingRight: '100px',
                              }}
                           >
                              {getProductDetails(product) &&
                                 formatFloatAsCurrencySymbol(
                                    isNaN(getProductDetails(product).count)
                                       ? 0
                                       : getProductDetails(product).count *
                                            (product.BasePrice ?? 0),
                                    currencyObject,
                                 )}
                           </td>
                        </tr>
                     )
                  })}
               </tbody>
            </table>
         </div>
      </div>
   )
}

export default MultiProductTable
